import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, RefreshCw } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { RingGauge } from "../../components/neu/RingGauge";

// ظل 3D مطابق لبطاقات الصفحة الرئيسية (TodayCard/StatCard).
const CARD_3D = {
  boxShadow:
    "0 14px 28px rgba(0,0,0,0.32), 0 8px 12px rgba(0,0,0,0.20),"
    + " inset 0 1.5px 0 rgba(255,255,255,0.28), inset 0 -4px 10px rgba(0,0,0,0.18)",
};

// عدّاد الرواتب اليومي بثيم Neu (نفس أسلوب الصفحة الرئيسية) — يتبع الفترة المحاسبية للفرع.
export const PayrollCounter = ({ info, onReload, busy }) => {
  const { t } = useTranslation();
  const d = info || {};
  const percent = Math.min(Math.max(Number(d.percent || 0), 0), 100);
  const [barW, setBarW] = useState(0);
  // المربع عنصر بنيوي دائم في الصفحة؛ يُرسم الإطار دائمًا وتُملأ الأرقام بالأنيميشن عند توفّر البيانات.
  useEffect(() => {
    const id = requestAnimationFrame(() => setBarW(percent));
    return () => cancelAnimationFrame(id);
  }, [percent]);

  return (
    <NeuCard className="p-4" style={CARD_3D} data-testid="payroll-counter">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex items-center gap-5">
          <RingGauge value={d.current_day || 0} total={d.days_in_month || 0} size={132} tone="#BD9B57"
                     label={t("payrollCounter.dayLabel")} icon={CalendarClock} testId="payroll-counter-ring" />
          <div className="grid gap-1.5">
            <p className="label-xs text-sm">{t("payrollCounter.title")}</p>
            <p className="font-mono text-xl font-black leading-tight" data-testid="payroll-counter-day">
              {t("payrollCounter.dayOf", { day: d.current_day || 0, total: d.days_in_month || 0 })}
            </p>
            <p className="font-mono text-xs font-semibold text-muted-foreground" dir="ltr">
              {d.period_start || "—"} → {d.period_end || "—"}
            </p>
            <p className="text-xs font-bold text-muted-foreground">
              {t("payrollCounter.payDay", { day: d.payroll_day ?? "—" })}
              {d.branch_name ? ` · ${d.branch_name}` : ""}
            </p>
          </div>
        </div>
        <NeuButton className="flex items-center gap-2" data-testid="payroll-counter-reload"
                   disabled={busy} onClick={onReload}>
          <RefreshCw className="h-4 w-4" /> {t("payrollCounter.refresh")}
        </NeuButton>
      </div>

      <div className="mt-4 rounded-3xl bg-background p-4 shadow-neu-in" data-testid="payroll-month-progress">
        <div className="mb-2 flex items-center justify-between text-xs font-bold">
          <span className="text-muted-foreground">{t("payrollCounter.monthProgress")}</span>
          <span className="font-mono text-[#BD9B57]" dir="ltr" data-testid="payroll-counter-percent">
            {percent.toFixed(1)}%
          </span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-foreground/[.06]">
          <div className="h-full rounded-full bg-gradient-to-l from-[#BD9B57] to-[#8a6f3c]"
               style={{ width: `${barW}%`, transition: "width 900ms ease" }} />
        </div>
        <p className="mt-2 text-center text-xs font-semibold text-muted-foreground">
          {t("payrollCounter.remaining", { count: d.remaining_days || 0 })}
        </p>
      </div>
    </NeuCard>
  );
};
